import { stringify } from "ajv";
import moment from "moment/moment";
import { convertFromRaw } from "draft-js";
import { Slide, toast } from "react-toastify";
import { formatDistanceToNow } from "date-fns";

import {
  EMPTY_STRING,
  PAYLOAD_KEYS,
  BUILD_QUERY_KEYS,
  SUBSCRIPTION_PLAN_MAPPING,
  SUBSCRIPTION_PLAN_TYPE_MAPPING,
} from "../constants";
import { PROPERTY_IMAGE_TYPES, USER_ROLE } from "../system/global-enums";

export const getOptionsFromEnums = (enums) => {
  return Object.entries(enums).map(([key, value]) => ({
    label: value,
    value: Number(key),
  }));
};

export const getSelectOptions = (data = [], label, value) =>
  data.map((option) => ({
    ...option,
    label: option[label],
    value: option[value],
  }));

export const dateToUnixTimestamp = (date) => {
  if (!date) return null;
  return Math.floor(date.getTime() / 1000);
};
export const unixTimestampToDate = (timestamp) => {
  if (!timestamp) return null;
  return new Date(timestamp * 1000);
};

export const formatDate = (date) => {
  if (!date) return;
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-indexed
  const year = date.getFullYear();

  return `${day}-${month}-${year}`;
};

export const objectToFormData = (obj, coverIndex = 0, sendCover = false) => {
  const formData = new FormData();

  const appendNestedObject = (baseKey, value) => {
    if (typeof value === "object" && !Array.isArray(value)) {
      // Handle nested objects
      for (const nestedKey in value) {
        appendNestedObject(`${baseKey}.${nestedKey}`, value[nestedKey]);
      }
    } else if (Array.isArray(value)) {
      // Handle arrays
      value.forEach((item, index) => {
        if (typeof item === "object") {
          // Recursively process array items if they are objects
          appendNestedObject(`${baseKey}[${index}]`, item);
        } else {
          // Append primitive values
          formData.append(`${baseKey}[${index}]`, item);
        }
      });
    } else {
      // Append primitive values
      formData.append(baseKey, value);
    }
  };

  // Append fields to FormData
  for (const key in obj) {
    if (typeof obj[key] === "object" && !Array.isArray(obj[key])) {
      // Handle nested objects
      appendNestedObject(key, obj[key]);
    } else if (Array.isArray(obj[key])) {
      obj[key].forEach((item, index) => {
        if (item instanceof File || item instanceof Blob) {
          // Append image files directly
          formData.append(
            index !== coverIndex || !sendCover
              ? `${key}[${index}]`
              : "coverImage",
            item
          );
        } else if (typeof item === "object") {
          // Recursively process objects in arrays
          appendNestedObject(`${key}[${index}]`, item);
        } else {
          // Append other items as JSON string
          formData.append(`${key}[${index}]`, JSON.stringify(item));
        }
      });
    } else {
      formData.append(key, obj[key]);
    }
  }

  return formData;
};

export const getLabelsByIds = (array = [], id, labelKey, idKey = "id") => {
  if (!id) return;
  if (typeof id === "object") {
    return array
      .filter((instance) => id.includes(instance[idKey]))
      .map((filteredInstance) => filteredInstance[labelKey]);
  } else {
    return array.length > 0
      ? array.find((instance) => instance[idKey] === id)?.[labelKey]
      : EMPTY_STRING;
  }
};
export const getLabelsByEnums = (enums, id) => {
  if (typeof id === "object") {
    return id.map((instance) => enums[instance]);
  } else {
    return enums[id];
  }
};

export const boolToZeroOrOne = (value) => {
  if (value) return 1;
  return 0;
};

export const getPageNumber = (type, data, pagination) => {
  return type === PAYLOAD_KEYS.PAGE_SIZE || type === PAYLOAD_KEYS.SEARCH
    ? 0
    : type === PAYLOAD_KEYS.PAGE_NUMBER
    ? data
    : pagination.pageNumber;
};

export const customToast = (
  AllErrorMessages,
  type = toast.TYPE,
  time = 2000,
  position = "top-right"
) => {
  const options = {
    autoClose: time,
    type,
    hideProgressBar: true,
    position,
    pauseOnHover: true,
    transition: Slide,
    theme: "colored",
    className: "color",
  };
  const message = AllErrorMessages;
  toast(message, options);
};

export const areArraysEqual = (arr1, arr2) => {
  // Check if lengths are different
  if (arr1.length !== arr2.length) return false;

  // Compare each element
  return arr1.every((value, index) => value === arr2[index]);
};

export const convertCurrency = (
  amount,
  source,
  targetCurrency,
  allCurrencies
) => {
  // Find the source currency object
  const sourceCurrency = allCurrencies.find(
    (currency) => currency.code === source
  );

  // If source currency is not found, conversion is not possible
  if (!sourceCurrency || !targetCurrency) {
    return 0; // Return 0 or handle the error as needed
  }

  // Find the base currency object for the source currency
  const sourceBaseCurrency = allCurrencies.find(
    (currency) => currency.id === sourceCurrency.baseCurrency
  );

  // If the base currency is not found, conversion is not possible
  if (!sourceBaseCurrency) {
    return 0; // Return 0 or handle the error as needed
  }

  // Convert source currency to base currency
  const amountInBase = amount / sourceCurrency.conversionRate;

  // Convert base currency to target currency
  const amountInTarget = amountInBase * targetCurrency.conversionRate;

  return formatToTwoDecimalPlaces(amountInTarget);
};

export const buildFilterQuery = (data, searchFields) => {
  if (data === EMPTY_STRING) {
    return EMPTY_STRING;
  }
  const value = stringify(data);
  const sanitizedValue =
    typeof value === BUILD_QUERY_KEYS.STRING ? value : String(value); // Convert to string if necessary.

  return searchFields
    .map((field) => {
      const fieldValue =
        field === BUILD_QUERY_KEYS.PHONE_NUMBER
          ? sanitizedValue?.replace(/\+/g, "")
          : sanitizedValue;
      return `${field}.${BUILD_QUERY_KEYS.CONTAINS}(${fieldValue})`;
    })
    .join(" || ");
};

export const relativeDateTime = (epoch) => {
  const date = new Date(epoch * 1000);
  const relativeTime = formatDistanceToNow(date, { addSuffix: true });
  return relativeTime;
};

//Return Unix timestamp in seconds for the start of the day (00:00:00)
export const convertFromDateToUnix = (fromDate) => {
  const date = new Date(fromDate);
  date.setHours(0, 0, 0, 0);
  return date.getTime() / 1000;
};

//Return Unix timestamp in seconds for the end of the day (23:59:59)
export const convertToDateToUnix = (toDate) => {
  const date = new Date(toDate);
  date.setHours(23, 59, 59, 0);
  return date.getTime() / 1000;
};

export const formatUnixTimestampToDate = (date) => {
  return moment.unix(date).format("DD-MM-YYYY");
};

export const getFormattedValue = (
  value,
  formatFunction = null,
  formatValue = null
) => {
  if (!value) return "-";

  if (formatFunction) {
    return formatFunction(value);
  } else {
    return formatValue || value;
  }
};

export const hasUserAccess = (userRole) => {
  return userRole === USER_ROLE.USER;
};

export const hasAgentAccess = (userRole) => {
  return userRole === USER_ROLE.AGENT;
};

export const hasAdminAccess = (userRole) => {
  return userRole === USER_ROLE.SUPER_ADMIN || userRole === USER_ROLE.ADMIN;
};

export const formatToTwoDecimalPlaces = (value) => {
  return parseFloat(value).toFixed(2);
};

export const isEditorEmpty = (rawContent) => {
  try {
    const contentState = convertFromRaw(JSON.parse(rawContent));
    const blocks = contentState.getBlocksAsArray();
    return blocks.every((block) => !block.getText().trim());
  } catch (error) {
    console.error("Error checking content:", error);
    return true; // Consider content empty if there's an error
  }
};

export const getTimezoneOffSet = () => {
  const offsetMinutes = new Date().getTimezoneOffset();
  //Determine the sign
  const sign = offsetMinutes > 0 ? "-" : "+";

  const minutes = Math.abs(offsetMinutes);
  //Convert the offsetMinutes to hours and minutes
  const offsetHours = Math.floor(minutes / 60);
  const offsetMinutesRemainder = Math.floor(minutes % 60);
  //Format the offset as "-HH:mm" or "+HH:mm"
  const formattedOffset = `${sign}${offsetHours
    .toString()
    .padStart(2, "0")}:${offsetMinutesRemainder.toString().padStart(2, "0")}`;

  return formattedOffset;
};

export const truncateText = (text, length = 15) => {
  if (text?.length > length) {
    return text.substring(0, length) + "...";
  }
  return text;
};

export const formatRating = (rating) => {
  let value = Number(rating);
  value = value % 1 === 0 ? `${value}` : value.toFixed(1);
  return `${value}/5`;
};

export const getSortBy = (sorting) => {
  if (!sorting) return "";

  // Replace underscores with dots in sorting.id
  const sortedId = sorting.id.replace(/_/g, ".");
  const sortOrder = sorting.desc ? "desc" : "asc";

  return `${sortedId} ${sortOrder}`.trim();
};

export const customStringSorting = (rowA, rowB, id) => {
  let a = rowA.getValue(id)?.toUpperCase();
  let b = rowB.getValue(id)?.toUpperCase();

  if (a > b) return 1;
  if (a < b) return -1;
  return 0;
};

export const formatSubscriptionDetails = (subscription) => {
  if (!subscription) return "Not Subscribed";

  const { plan, planType, isFreeTrialPeriod } = subscription;
  const planDetails = `${SUBSCRIPTION_PLAN_MAPPING[plan]} - ${SUBSCRIPTION_PLAN_TYPE_MAPPING[planType]}`;
  return isFreeTrialPeriod ? `${planDetails} (Free Trial)` : planDetails;
};

export const fileName = (componentName) => {
  const today = new Date();
  const fileName = `${componentName}_${String(today.getDate()).padStart(
    2,
    "0"
  )}-${String(today.getMonth() + 1).padStart(
    2,
    "0"
  )}-${today.getFullYear()}_${String(today.getHours()).padStart(
    2,
    "0"
  )}-${String(today.getMinutes()).padStart(2, "0")}`;

  return fileName;
};

export const numberFormatting = (value) => {
  return value ? new Intl.NumberFormat().format(value) : value;
};

/**
 * Cover (or first) image URL for property list cards.
 * Prefers thumbnailUrl; falls back to full url when thumbnail is null/missing.
 */
export const getPropertyListImageUrl = (images = []) => {
  const coverImage =
    images.find((img) => img.type === PROPERTY_IMAGE_TYPES.COVER_IMAGE) ??
    images[0];

  return coverImage?.thumbnailUrl ?? coverImage?.url;
};
